<script>
/**
 * Incoming-CC mapping badge (MDL-267). Wraps a mappable control (the
 * default slot) and overlays an assignment badge when the CC overlay is
 * active: "CC 14" when mapped, "+ CC" when not. Clicking the badge opens
 * the assignment popover.
 *
 * Renders nothing extra unless BOTH the connected device reports the
 * `cc_mapping` capability and the patch editor's overlay toggle is on.
 *
 * Also resolves the live incoming value for this mapping (MDL-269): when
 * the device echoes a parameterChanged for this CC, the scaled held value
 * is provided to the slotted PedalKnob as `ccLiveValue` (ghost marker) and
 * shown on the badge — persisting until a newer value supersedes it, since
 * the device holds the override. The badge additionally pulses only while
 * the CC is actively arriving. Display-only — nothing writes into the effect.
 *
 * Injected (all provided by the patch editor shell):
 *   capabilities   — { capabilities: { cc_mapping?: {} } }
 *   ccOverlay      — reactive { active: Boolean }
 *   ccPatchEffects — all effects in the patch (for the in-use check)
 *   ccLiveValues   — live store from createCcLiveStore() (may be absent)
 */
import { computed } from 'vue';
import CcAssignPopover from './CcAssignPopover.vue';
import { isCcMappingSupported, paramSpec, getMapping, scaleCcValue } from '../js/ccMapping.js';

export default {
  components: { CcAssignPopover },

  inject: {
    capabilities: { default: () => ({ capabilities: {} }) },
    ccOverlay: { default: () => ({ active: false }) },
    ccPatchEffects: { default: () => [] },
    ccLiveValues: { default: null },
  },

  provide() {
    return {
      // Consumed by the slotted PedalKnob to render the ghost marker.
      ccLiveValue: computed(() => this.liveScaled),
    };
  },

  props: {
    effect: {
      type: Object,
      required: true
    },
    param: {
      type: String,
      required: true
    },
    // How to indicate an assignment when the overlay is off:
    //  'label' — tint the wrapped control's label with the effect colour
    //            (for knobs, whose label sits above the ring)
    //  'dot'   — a small corner dot (for the mute button, which has no label)
    marker: {
      type: String,
      default: 'label',
      validator: v => ['label', 'dot'].includes(v)
    }
  },

  data() {
    return { open: false };
  },

  computed: {
    visible() {
      return (
        isCcMappingSupported(this.capabilities) &&
        this.ccOverlay.active &&
        paramSpec(this.effect, this.param) !== null
      );
    },

    mapping() {
      return getMapping(this.effect, this.param);
    },

    // Raw value (0–127) the device is currently holding on this mapping's
    // CC. Persists until a newer value supersedes it (the override is held
    // on the device), so the ghost/readout stay put after the sweep stops.
    liveRaw() {
      if (!this.ccLiveValues || !this.mapping) return null;
      const raw = this.ccLiveValues.values[this.mapping.cc];
      return raw === undefined ? null : raw;
    },

    // True only while this mapping's CC is actively arriving — drives the
    // "moving right now" pulse, independent of the persisted held value.
    liveActive() {
      return !!(this.ccLiveValues && this.mapping && this.ccLiveValues.active?.[this.mapping.cc]);
    },

    // The value the firmware is applying — same range-aware scaling maths,
    // shared via the vector-fixture contract, so ghost and device agree.
    liveScaled() {
      if (this.liveRaw === null) return null;
      const spec = paramSpec(this.effect, this.param);
      if (!spec) return null;
      const { lo, hi } = this.mapping;
      return scaleCcValue(spec, this.liveRaw, lo, hi, this.effect.clock_sync === true);
    },

    badgeText() {
      if (!this.mapping) return '+ CC';
      if (this.liveRaw !== null) return `CC ${this.mapping.cc} · ${this.liveRaw}`;
      return `CC ${this.mapping.cc}`;
    },

    // A persistent "this control is mapped" dot, shown when the overlay is
    // OFF (when it's on, the full badge stands in). Lets you see at a glance
    // which controls have an incoming CC without opening the overlay.
    showMarker() {
      return !this.ccOverlay.active && this.mapping !== null;
    }
  },

  watch: {
    // Close the assignment modal if the overlay is switched off underneath it.
    visible(nowVisible) {
      if (!nowVisible) this.open = false;
    }
  }
}
</script>
<template>
  <div class="cc-mappable" :class="{ 'cc-mappable--mapped': showMarker && marker === 'label' }">
    <slot />
    <span
      v-if="showMarker && marker === 'dot'"
      class="cc-marker"
      :title="`Mapped to CC ${mapping.cc}`"
    ></span>
    <button
      v-if="visible"
      type="button"
      class="cc-badge"
      :class="{ 'cc-badge--assigned': mapping, 'cc-badge--open': open, 'cc-badge--live': liveActive }"
      :aria-label="mapping ? `Mapped to CC ${mapping.cc}` : 'Assign a CC'"
      @click.stop="open = !open"
    >{{ badgeText }}</button>
    <CcAssignPopover
      v-if="open"
      :effect="effect"
      :param="param"
      :effects="ccPatchEffects"
      @close="open = false"
    />
  </div>
</template>
